import { useMemo, useState } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { Card, Flex, Select, Table, Tag } from 'antd';
import { ColumnsType } from 'antd/es/table';

import Button from '../../../custom/button/button';
import { useDownload } from '../../../hooks/useDownload';
import { getAllStudentUser } from '../../userManagement/request';
import { getAllAcademicSession, getAllApplicationBatch } from '../../../requests';
import api from '../../../utils/api';
import { buildQuery } from '../../../utils/buildQuery';

// The backend silently caps PageSize (observed: requesting 100000 only ever
// returns 500 rows), so a single big request under-counts. Every page is
// fetched in a loop instead, driven by the server's own totalSize/totalRecords,
// until nothing more comes back.
const PAGE_SIZE = 500;
const MAX_PAGES = 200; // safety guard against an unexpected infinite loop

type EnrichedApplicant = User & {
  hasPaidApplicationFee: boolean;
  hasPaidAcceptanceFee: boolean;
};

interface ProgramSummaryRow {
  programKey: string;
  programName: string;
  programCode: string;
  total: number;
  admitted: number;
  rejected: number;
  pending: number;
  applicationFeePaid: number;
  acceptanceFeePaid: number;
}

const getProgramKey = (applicant: Pick<User, 'programId' | 'programTypeId' | 'programCode' | 'programName'>) =>
  applicant.programId || applicant.programTypeId || applicant.programCode
    ? `${applicant.programId}|${applicant.programTypeId}|${applicant.programCode ?? ''}`
    : `name:${applicant.programName || 'Unspecified'}`;

const isAdmittedStatus = (val: User['isAdmitted']) => String(val ?? '').toLowerCase() === 'accept';
const isRejectedStatus = (val: User['isAdmitted']) => {
  const status = String(val ?? '').toLowerCase();
  return status === 'reject' || status === 'rejected';
};

const fetchAllApplicants = async (sessionId?: number, applicationBatchId?: number): Promise<User[]> => {
  const collected: User[] = [];
  let page = 1;
  let total = Infinity;
  while (collected.length < total && page <= MAX_PAGES) {
    const res = await getAllStudentUser({ PageNumber: page, PageSize: PAGE_SIZE, sessionId, applicationBatchId });
    const items = (res?.data as User[]) ?? [];
    total = res?.totalSize ?? 0;
    if (!items.length) break;
    collected.push(...items);
    page += 1;
  }
  return collected;
};

// /Authorization/GetApplicantsFilter is the only endpoint that also carries
// hasPaidApplicationFee / hasPaidAcceptanceFee per applicant, so it's fetched
// separately (same page-looping approach) and merged in by applicantId.
const getApplicantsFilterPage = async (params: Record<string, any>) => {
  const url = buildQuery(params);
  return (await api.get(`/Authorization/GetApplicantsFilter${url}`))?.data;
};

interface PaymentInfo {
  hasPaidApplicationFee: boolean;
  hasPaidAcceptanceFee: boolean;
}

const fetchAllPaymentStatuses = async (
  sessionId?: number,
  applicationBatchId?: number,
): Promise<Map<string, PaymentInfo>> => {
  const map = new Map<string, PaymentInfo>();
  let page = 1;
  let total = Infinity;
  let collected = 0;
  while (collected < total && page <= MAX_PAGES) {
    const res = await getApplicantsFilterPage({
      PageNumber: page,
      PageSize: PAGE_SIZE,
      SessionId: sessionId,
      ApplicationBatchId: applicationBatchId,
    });
    const items = (res?.data?.data as any[]) ?? [];
    total = res?.data?.totalRecords ?? 0;
    if (!items.length) break;
    items.forEach((r) => {
      map.set(r.applicantId, {
        hasPaidApplicationFee: Boolean(r.hasPaidApplicationFee),
        hasPaidAcceptanceFee: Boolean(r.hasPaidAcceptanceFee),
      });
    });
    collected += items.length;
    page += 1;
  }
  return map;
};

const applicantColumns: ColumnsType<EnrichedApplicant> = [
  { key: 'appNo', title: 'Application No', dataIndex: 'applicationNumber' },
  { key: 'firstName', title: 'First Name', dataIndex: 'firstName' },
  { key: 'lastName', title: 'Last Name', dataIndex: 'lastName' },
  { key: 'programCode', title: 'Program Code', dataIndex: 'programCode' },
  { key: 'email', title: 'Email', dataIndex: 'email' },
  { key: 'phoneNumber', title: 'Phone', dataIndex: 'phoneNumber' },
  {
    key: 'status',
    title: 'Status',
    dataIndex: 'isAdmitted',
    render: (val: User['isAdmitted']) => {
      if (isAdmittedStatus(val)) return <Tag color="success">Admitted</Tag>;
      if (isRejectedStatus(val)) return <Tag color="error">Rejected</Tag>;
      return <Tag color="default">Pending</Tag>;
    },
  },
  {
    key: 'appFee',
    title: 'App. Fee',
    dataIndex: 'hasPaidApplicationFee',
    render: (v: boolean) => <Tag color={v ? 'success' : 'warning'}>{v ? 'Paid' : 'Unpaid'}</Tag>,
  },
  {
    key: 'accFee',
    title: 'Acceptance Fee',
    dataIndex: 'hasPaidAcceptanceFee',
    render: (v: boolean) => <Tag color={v ? 'success' : 'warning'}>{v ? 'Paid' : 'Unpaid'}</Tag>,
  },
];

const ProgramSummary = () => {
  const [sessionId, setSessionId] = useState<number | undefined>(undefined);
  const [applicationBatchId, setApplicationBatchId] = useState<number | undefined>(undefined);
  const [expandedProgram, setExpandedProgram] = useState<string | null>(null);
  const { downloadExcelFile } = useDownload();

  const [sessionQuery, batchQuery] = useQueries({
    queries: [
      { queryKey: ['sessions'], queryFn: () => getAllAcademicSession() },
      { queryKey: ['application-batches'], queryFn: getAllApplicationBatch },
    ],
  });

  const sessionOptions = (sessionQuery.data?.data as any[])?.map((s) => ({ label: s.name, value: s.id }));
  const batchOptions = (batchQuery.data?.data as any[])?.map((b) => ({ label: b.batchName, value: b.id }));

  // Fetch every applicant (fully paginated) and enrich with payment status,
  // then group/count client-side by the program's identity (programId +
  // programTypeId + programCode), not by programName — different programs
  // can share a display name, and programId/programTypeId alone aren't unique
  // either, so a name- or id-pair-only key can silently merge distinct programs.
  const { data: applicants = [], isLoading, isFetching } = useQuery({
    queryKey: ['program-summary-applicants', sessionId, applicationBatchId],
    queryFn: async (): Promise<EnrichedApplicant[]> => {
      const [users, paymentMap] = await Promise.all([
        fetchAllApplicants(sessionId, applicationBatchId),
        fetchAllPaymentStatuses(sessionId, applicationBatchId),
      ]);
      return users.map((u) => ({
        ...u,
        hasPaidApplicationFee: paymentMap.get(u.applicantId)?.hasPaidApplicationFee ?? false,
        hasPaidAcceptanceFee: paymentMap.get(u.applicantId)?.hasPaidAcceptanceFee ?? false,
      }));
    },
  });

  const summaryRows = useMemo<ProgramSummaryRow[]>(() => {
    const map = new Map<string, ProgramSummaryRow>();
    applicants.forEach((applicant) => {
      const key = getProgramKey(applicant);
      if (!map.has(key)) {
        map.set(key, {
          programKey: key,
          programName: applicant.programName || 'Unspecified',
          programCode: applicant.programCode || '',
          total: 0,
          admitted: 0,
          rejected: 0,
          pending: 0,
          applicationFeePaid: 0,
          acceptanceFeePaid: 0,
        });
      }
      const row = map.get(key) as ProgramSummaryRow;
      row.total += 1;
      if (isAdmittedStatus(applicant.isAdmitted)) row.admitted += 1;
      else if (isRejectedStatus(applicant.isAdmitted)) row.rejected += 1;
      else row.pending += 1;
      if (applicant.hasPaidApplicationFee) row.applicationFeePaid += 1;
      if (applicant.hasPaidAcceptanceFee) row.acceptanceFeePaid += 1;
    });
    return Array.from(map.values()).sort((a, b) => b.total - a.total);
  }, [applicants]);

  const columns: ColumnsType<ProgramSummaryRow> = [
    { key: 'programName', title: 'Program', dataIndex: 'programName' },
    { key: 'programCode', title: 'Program Code', dataIndex: 'programCode' },
    { key: 'total', title: 'Total Applied', dataIndex: 'total', sorter: (a, b) => a.total - b.total, defaultSortOrder: 'descend' },
    { key: 'admitted', title: 'Admitted', dataIndex: 'admitted', render: (v: number) => <Tag color="success">{v}</Tag> },
    { key: 'pending', title: 'Pending', dataIndex: 'pending', render: (v: number) => <Tag color="default">{v}</Tag> },
    { key: 'rejected', title: 'Rejected', dataIndex: 'rejected', render: (v: number) => <Tag color="error">{v}</Tag> },
    { key: 'applicationFeePaid', title: 'Application Fee Paid', dataIndex: 'applicationFeePaid' },
    { key: 'acceptanceFeePaid', title: 'Acceptance Fee Paid', dataIndex: 'acceptanceFeePaid' },
  ];

  const handleDownloadSummary = () => {
    const header = 'Program,Program Code,Total Applied,Admitted,Pending,Rejected,Application Fee Paid,Acceptance Fee Paid\n';
    const rows = summaryRows
      .map(
        (r) =>
          `"${r.programName.replace(/"/g, '""')}","${r.programCode.replace(/"/g, '""')}",${r.total},${r.admitted},${r.pending},${r.rejected},${r.applicationFeePaid},${r.acceptanceFeePaid}`,
      )
      .join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv;charset=utf-8;' });
    downloadExcelFile(blob, 'Applicants_By_Program_Summary.csv');
  };

  return (
    <div>
      <section className="space-between">
        <h3>Applicants by Program</h3>
        <Button text="Download Summary (CSV)" onClick={handleDownloadSummary} disabled={!summaryRows.length} />
      </section>

      <br />

      <Card bordered={false}>
        <div style={{ marginBottom: 16 }}>
          <Flex gap="8px" wrap="wrap">
            <Select
              placeholder="Session"
              allowClear
              options={sessionOptions}
              loading={sessionQuery.isLoading}
              style={{ minWidth: 200 }}
              onChange={(v) => setSessionId(v)}
            />
            <Select
              placeholder="Application Batch"
              allowClear
              options={batchOptions}
              loading={batchQuery.isLoading}
              style={{ minWidth: 220 }}
              onChange={(v) => setApplicationBatchId(v)}
            />
          </Flex>
        </div>

        <p style={{ marginBottom: 12, color: '#6b7280', fontSize: 13 }}>
          {isFetching
            ? 'Loading applicants…'
            : `${applicants.length} applicant(s) across ${summaryRows.length} program(s).`}
        </p>

        <Table
          dataSource={summaryRows}
          columns={columns}
          rowKey={(r) => r.programKey}
          loading={isLoading}
          pagination={false}
          scroll={{ x: true }}
          locale={{ emptyText: 'No applicants found for the selected filters' }}
          expandable={{
            expandedRowKeys: expandedProgram ? [expandedProgram] : [],
            onExpand: (expanded, record) => setExpandedProgram(expanded ? record.programKey : null),
            expandedRowRender: (record) => (
              <Table
                dataSource={applicants.filter((a) => getProgramKey(a) === record.programKey)}
                columns={applicantColumns}
                rowKey={(a) => a.applicantId}
                pagination={{ pageSize: 10 }}
                size="small"
                scroll={{ x: true }}
              />
            ),
          }}
        />
      </Card>
    </div>
  );
};

export default ProgramSummary;
